import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const { companySlug } = useParams()
  const navigate = useNavigate()

  // Check for existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('token')
    const savedUser = localStorage.getItem('user')
    
    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser)
        setUser(parsedUser)
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      } catch (e) {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      }
    }
    setLoading(false)
  }, [])

  const login = useCallback(async (email, password) => {
    try {
      const response = await api.post(`/${companySlug}/auth/login`, {
        email,
        password
      })
      
      const { success, access_token, employee_id, email: userEmail, role, requires_password_change, message } = response.data
      
      if (!success) {
        return { success: false, message, requires_password_change }
      }
      
      const userData = {
        id: employee_id,
        email: userEmail,
        role,
        companySlug
      }
      
      localStorage.setItem('token', access_token)
      localStorage.setItem('user', JSON.stringify(userData))
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
      setUser(userData)
      
      return { success: true, requires_password_change, role }
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed'
      return { success: false, message }
    }
  }, [companySlug])

  const logout = useCallback(async () => {
    try {
      await api.post(`/${companySlug}/auth/logout`)
    } catch (e) {
      // Ignore error
    } finally {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      setUser(null)
      delete api.defaults.headers.common['Authorization']
      navigate(`/${companySlug}/login`)
    }
  }, [companySlug, navigate])

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      await api.post(`/${companySlug}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      })
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password'
      return { success: false, message }
    }
  }, [companySlug])

  const resetPassword = useCallback(async (email) => {
    try {
      await api.post(`/${companySlug}/auth/reset-password`, { email })
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reset password'
      return { success: false, message }
    }
  }, [companySlug])

  const value = {
    user,
    loading,
    login,
    logout,
    changePassword,
    resetPassword,
    isAuthenticated: !!user,
    isHRAdmin: user?.role === 'hr_admin',
    isAdmin: user?.role === 'admin',
    isReviewer: user?.role === 'admin' || user?.role === 'hr_admin'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
